import * as React from "react";
import { useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useOrigins } from "@/hooks/useReferenceData";
import { toUserFacingErrorMessage } from "@/lib/errors";
import { useAppLocale } from "@/lib/i18n";
import { m } from "@/paraglide/messages.js";

interface OriginManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function OriginManagementDialog({ open, onOpenChange }: OriginManagementDialogProps) {
  useAppLocale();
  const origins = useOrigins();
  const createOrigin = useMutation(api.products.createOrigin);
  const updateOrigin = useMutation(api.products.updateOrigin);
  const deleteOrigin = useMutation(api.products.deleteOrigin);

  const [search, setSearch] = React.useState("");
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const filtered = React.useMemo(() => {
    if (!origins) return [];
    if (!search) return origins;
    const q = search.toLowerCase();
    return origins.filter((o: any) => o.name?.toLowerCase().includes(q));
  }, [origins, search]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(m.name_required_simple());
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateOrigin({ id: editingId as any, name: name.trim(), description: description || undefined });
        toast.success(m.origin_updated());
      } else {
        await createOrigin({ name: name.trim(), description: description || undefined });
        toast.success(m.origin_created());
      }
      resetForm();
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(error, "Failed to save origin", {
          unauthorizedMessage:
            m.inventory_products_unauthorized(),
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (origin: any) => {
    setEditingId(origin._id);
    setName(origin.name);
    setDescription(origin.description ?? "");
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteOrigin({ id: id as any });
      toast.success(m.origin_deleted());
      if (editingId === id) resetForm();
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(error, "Failed to delete origin", {
          unauthorizedMessage:
            m.inventory_products_unauthorized(),
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{m.manage_origins()}</DialogTitle>
          <DialogDescription>{m.manage_origins_description()}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-3 rounded-md border p-3">
            <div className="grid gap-2">
              <Label>{editingId ? m.edit_origin() : m.add_origin()}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={m.origin_name()} />
            </div>
            <div className="grid gap-2">
              <Label>{m.description()}</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={m.optional_description()} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => void handleSave()} disabled={saving}>
                {saving ? m.saving() : editingId ? m.update() : m.add()}
              </Button>
              {editingId && (
                <Button size="sm" variant="outline" onClick={resetForm}>{m.cancel()}</Button>
              )}
            </div>
          </div>

          <Input placeholder={m.search_origins()} value={search} onChange={(e) => setSearch(e.target.value)} />

          <div className="max-h-[300px] space-y-1 overflow-y-auto">
            {filtered.map((origin: any) => (
              <div
                key={origin._id}
                className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-muted/50"
              >
                <div>
                  <p className="text-sm font-medium">{origin.name}</p>
                  {origin.description && (
                    <p className="text-xs text-muted-foreground">{origin.description}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="icon-xs" variant="ghost" onClick={() => handleEdit(origin)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon-xs" variant="ghost" aria-label={`Delete ${origin.name}`}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete origin?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will remove <span className="font-medium">{origin.name}</span> from the catalog. Regions and producers tied to this origin will need to be reassigned. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          variant="destructive"
                          onClick={() => void handleDelete(origin._id)}
                        >
                          Delete origin
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">{m.no_origins_found()}</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
